import React, { useEffect, useState } from 'react';
import SaveTwoToneIcon from '@mui/icons-material/SaveTwoTone';
import {
  Box,
  Button,
  Checkbox,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import ArrowForwardIosSharpIcon from '@mui/icons-material/ArrowForwardIosSharp';
import MuiAccordion from '@mui/material/Accordion';
import MuiAccordionSummary from '@mui/material/AccordionSummary';
import MuiAccordionDetails from '@mui/material/AccordionDetails';
import PropTypes from 'prop-types';

import { CustomInputBase } from '../../components/searchbar';
import RoleHeader from './RoleHeader';

const Accordion = styled((props) => <MuiAccordion disableGutters elevation={0} square {...props} />)(({ theme }) => ({
  border: `1px solid ${theme.palette.divider}`,
  '&:not(:last-child)': {
    borderBottom: 0,
  },
  '&::before': {
    display: 'none',
  },
}));

const AccordionSummary = styled((props) => (
  <MuiAccordionSummary expandIcon={<ArrowForwardIosSharpIcon sx={{ fontSize: '0.9rem' }} />} {...props} />
))(({ theme }) => ({
  backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, .05)' : 'rgba(0, 0, 0, .03)',
  flexDirection: 'row-reverse',
  '& .MuiAccordionSummary-expandIconWrapper.Mui-expanded': {
    transform: 'rotate(90deg)',
  },
  '& .MuiAccordionSummary-content': {
    marginLeft: theme.spacing(1),
  },
}));

const AccordionDetails = styled(MuiAccordionDetails)(({ theme }) => ({
  padding: theme.spacing(2),
  borderTop: '1px solid rgba(0, 0, 0, .125)',
}));

export default function RoleAccordion({
  allRole,
  setAllRole,
  user,
  handleSave,
  handleUserInfo,
  changed,
  setChanged,
  loading,
  handelRef,
}) {
  const dioceseLevel = user && user.role.permissions.dioceseLevel.read;
  const canUpdateRole = user && user.role.permissions.permission.update;
  // only for permission
  const canUpdate = user && user.role.permissions.permission.update;

  const [expanded, setExpanded] = useState();
  const [tableRowQuery, setTableRowQuery] = useState('');
  const [tableRows, setTableRows] = useState([]);
  const [newTableRows, setNewTableRows] = useState([]);

  useEffect(() => {
    const all = tableRows;
    setNewTableRows(tableRowQuery ? all.filter((permission) => permission.amhName.includes(tableRowQuery)) : all);
  }, [tableRowQuery, tableRows]);

  const handleChange = (panel, resource) => (event, newExpanded) => {
    setExpanded(newExpanded ? panel : false);

    handelRef();
    setTableRows(resource);
    setNewTableRows(resource);
  };

  return (
    <>
      {allRole.length >= 1 ? (
        allRole.map((roleData) => (
          <Accordion
            sx={{
              bgcolor: (theme) => (theme.palette.mode === 'light' ? '#FEFEFE' : '#1F1F1F'),
            }}
            key={roleData.id}
            expanded={expanded === `${roleData.id}`}
            onChange={handleChange(roleData.id, roleData.permissions)}
          >
            <AccordionSummary aria-controls={`panel1d-content-${roleData.id}`} id={`summary-${roleData.id}`}>
              <RoleHeader roleData={roleData} dioceseLevel={dioceseLevel} handleUserInfo={handleUserInfo} />
            </AccordionSummary>
            <AccordionDetails>
              <TableContainer>
                <Table>
                  <TableHead sx={{ p: 0, backgroundColor: (theme) => `${theme.palette.background.paper}` }}>
                    <TableRow>
                      <TableCell sx={{ display: 'flex', justifyContent: 'space-between', gap: 5 }}>
                        <Typography>ምንጭ/ንብረት</Typography>
                        <CustomInputBase setQuery={setTableRowQuery} autoFocus={1} />
                      </TableCell>
                      <TableCell>አንብብ</TableCell>
                      <TableCell>መፍጠር</TableCell>
                      <TableCell>አርትዕ</TableCell>
                      <TableCell>ሰርዝ</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {newTableRows.map((permission) => (
                      <TableRow key={`${roleData.id}-${permission.id}`}>
                        <TableCell>
                          <Typography
                            sx={{
                              display: 'inline-block',
                              pl: 2,
                              pr: 2,
                              borderRadius: 5,
                              backgroundColor: (theme) => `${theme.palette.background.paper}`,
                            }}
                          >
                            {permission.amhName}

                          </Typography>
                          <Typography sx={{ display: { xs: 'none', md: 'block' } }} color="text.secondary">
                            {permission.resourceDescription}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Checkbox
                            disabled={!canUpdate}
                            checked={permission.read}
                            onChange={() => {
                              const newPermissions = [...allRole]; // Copy the permissions array
                              const roleIndex = newPermissions.findIndex((p) => p.id === roleData.id); // Find the index of the role
                              const permissionIndex = newPermissions[roleIndex].permissions.findIndex((p) => p.id === permission.id); // Find the index of the permission within the role
                              newPermissions[roleIndex].permissions[permissionIndex].read = !permission.read; // Update the read permission
                              const newRoleData = {
                                ...roleData,
                                permissions: newPermissions[roleIndex].permissions, // Update the permissions for the role
                                changed: true,
                              };
                              const newPermissionsState = [...allRole]; // Copy the allRole array
                              const index2 = newPermissionsState.findIndex((p) => p.id === roleData.id); // Find the index of the role within allRole
                              newPermissionsState[index2] = newRoleData; // Update the role in allRole
                              setAllRole(newPermissionsState); // Update the allRole state
                              const newChanged = { ...changed };
                              newChanged[roleData.id] = true; // Mark the role as changed
                              setChanged(newChanged); // Update the changed state
                            }}
                          />
                        </TableCell>
                        <TableCell>
                          <Checkbox
                            disabled={!canUpdate}
                            checked={permission.create}
                            onChange={() => {
                              const newPermissions = [...allRole]; // Copy the permissions array
                              const roleIndex = newPermissions.findIndex((p) => p.id === roleData.id); // Find the index of the role
                              const permissionIndex = newPermissions[roleIndex].permissions.findIndex((p) => p.id === permission.id); // Find the index of the permission within the role
                              newPermissions[roleIndex].permissions[permissionIndex].create = !permission.create; // Update the create permission
                              const newRoleData = {
                                ...roleData,
                                permissions: newPermissions[roleIndex].permissions, // Update the permissions for the role
                                changed: true,
                              };
                              const newPermissionsState = [...allRole]; // Copy the allRole array
                              const index2 = newPermissionsState.findIndex((p) => p.id === roleData.id); // Find the index of the role within allRole
                              newPermissionsState[index2] = newRoleData; // Update the role in allRole
                              setAllRole(newPermissionsState); // Update the allRole state
                              const newChanged = { ...changed };
                              newChanged[roleData.id] = true; // Mark the role as changed
                              setChanged(newChanged); // Update the changed state
                            }}
                          />
                        </TableCell>
                        <TableCell>
                          <Checkbox
                            disabled={!canUpdate}
                            checked={permission.update}
                            onChange={() => {
                              const newPermissions = [...allRole]; // Copy the permissions array
                              const roleIndex = newPermissions.findIndex((p) => p.id === roleData.id); // Find the index of the role
                              const permissionIndex = newPermissions[roleIndex].permissions.findIndex((p) => p.id === permission.id); // Find the index of the permission within the role
                              newPermissions[roleIndex].permissions[permissionIndex].update = !permission.update; // Update the update permission
                              const newRoleData = {
                                ...roleData,
                                permissions: newPermissions[roleIndex].permissions, // Update the permissions for the role
                                changed: true,
                              };
                              const newPermissionsState = [...allRole]; // Copy the allRole array
                              const index2 = newPermissionsState.findIndex((p) => p.id === roleData.id); // Find the index of the role within allRole
                              newPermissionsState[index2] = newRoleData; // Update the role in allRole
                              setAllRole(newPermissionsState); // Update the allRole state
                              const newChanged = { ...changed };
                              newChanged[roleData.id] = true; // Mark the role as changed
                              setChanged(newChanged); // Update the changed state
                            }}
                          />
                        </TableCell>
                        <TableCell>
                          <Checkbox
                            disabled={!canUpdate}
                            checked={permission.delete}
                            onChange={() => {
                              const newPermissions = [...allRole]; // Copy the permissions array
                              const roleIndex = newPermissions.findIndex((p) => p.id === roleData.id); // Find the index of the role
                              const permissionIndex = newPermissions[roleIndex].permissions.findIndex((p) => p.id === permission.id); // Find the index of the permission within the role
                              newPermissions[roleIndex].permissions[permissionIndex].delete = !permission.delete; // Update the delete permission
                              const newRoleData = {
                                ...roleData,
                                permissions: newPermissions[roleIndex].permissions, // Update the permissions for the role
                                changed: true,
                              };
                              const newPermissionsState = [...allRole]; // Copy the allRole array
                              const index2 = newPermissionsState.findIndex((p) => p.id === roleData.id); // Find the index of the role within allRole
                              newPermissionsState[index2] = newRoleData; // Update the role in allRole
                              setAllRole(newPermissionsState); // Update the allRole state
                              const newChanged = { ...changed };
                              newChanged[roleData.id] = true; // Mark the role as changed
                              setChanged(newChanged); // Update the changed state
                            }}
                          />
                        </TableCell>

                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'flex-end',
                  p: 2,
                }}
              >
                {loading ? (
                  <Button
                    variant="contained"
                    color="primary"
                    startIcon={<SaveTwoToneIcon />}
                    onClick={() => handleSave(roleData.id)}
                    disabled
                  >
                    በማስቀመጥ ላይ...
                  </Button>
                ) : (
                  <Button
                    variant="contained"
                    color="primary"
                    startIcon={<SaveTwoToneIcon />}
                    onClick={() => handleSave(roleData.id)}
                    disabled={!changed[roleData.id] || !canUpdateRole}
                  >
                    ለውጦችን አስቀምጥ
                  </Button>
                )}
              </Box>
            </AccordionDetails>
          </Accordion>
        ))
      ) : (
        <Box sx={{ textAlign: 'center', py: 2 }}>
          <Typography variant="h6" sx={{ p: 2 }} color="text.secondary">
            ምንም ሚናዎች አልተገኙም!
          </Typography>
          <Typography color="text.secondary">ነባሪ ፈቃዶቻቸውን ለማየት መጀመሪያ አንዳንድ ሚናዎችን ይፍጠሩ!</Typography>
        </Box>
      )}
    </>
  );
}

RoleAccordion.propTypes = {
  allRole: PropTypes.array.isRequired,
  setAllRole: PropTypes.func.isRequired,
  user: PropTypes.object.isRequired,
  handleSave: PropTypes.func.isRequired,
  handleUserInfo: PropTypes.func.isRequired,
  changed: PropTypes.bool.isRequired,
  setChanged: PropTypes.func.isRequired,
  loading: PropTypes.bool.isRequired,
  handelRef: PropTypes.func.isRequired,
};
